import { Link } from '@tanstack/react-router';
import { Button, Card, Empty, Flex, Space, Table, Tag, Tooltip, Typography, theme } from 'antd';
import {
  ApartmentOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  MessageOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ConversationOverviewChannel,
  ConversationOverviewFlag,
  ConversationOverviewListItem
} from '@/api/chat/interfaces';
import { IconCardTitle } from '@/components/ui/IconCardTitle';
import { formatDurationDisplay } from './constants';
import { ChannelTag, FlagTag, HandoffTag, HelpedTag, YesNoTag } from './tags';
import { useResponsivePinnedColumn } from '@/hooks/useResponsivePinnedColumn';

const { Text } = Typography;

type ConversationOverviewTableProps = {
  conversations: ConversationOverviewListItem[];
  clientsById: Map<string, string>;
  loading?: boolean;
  isError?: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize: number) => void;
};

/**
 * Paginated cross-tenant conversation list; red-flagged rows are tinted. Scrolls horizontally inside its card.
 */
export function ConversationOverviewTable({
  conversations,
  clientsById,
  loading,
  isError,
  page,
  pageSize,
  total,
  onPageChange
}: ConversationOverviewTableProps) {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  // Pinned tenant/actions columns would cover most of a phone screen, so only pin them from md up
  const pinColumn = useResponsivePinnedColumn();

  const columns: ColumnsType<ConversationOverviewListItem> = useMemo(
    () => [
      {
        title: t('conversationOverview.columns.tenant'),
        key: 'tenant',
        width: 180,
        fixed: pinColumn('left'),
        render: (_, row) => (
          <Flex align="center" gap={token.marginXS}>
            <ApartmentOutlined style={{ color: token.colorPrimary }} />
            <Text strong ellipsis={{ tooltip: true }} style={{ maxWidth: 140 }}>
              {clientsById.get(row.clientId) ?? row.clientId}
            </Text>
          </Flex>
        )
      },
      {
        title: t('conversationOverview.columns.channel'),
        dataIndex: 'channel',
        key: 'channel',
        width: 110,
        render: (channel: ConversationOverviewChannel) => <ChannelTag channel={channel} />
      },
      {
        title: t('conversationOverview.columns.startedAt'),
        dataIndex: 'startedAt',
        key: 'startedAt',
        width: 170,
        render: (value: string) => (
          <Flex align="center" gap={token.marginXXS + 2}>
            <ClockCircleOutlined style={{ color: token.colorTextSecondary }} />
            <Text style={{ whiteSpace: 'nowrap' }}>{dayjs(value).format('YYYY-MM-DD HH:mm')}</Text>
          </Flex>
        )
      },
      {
        title: t('conversationOverview.columns.duration'),
        key: 'duration',
        width: 170,
        render: (_, row) => {
          const display = formatDurationDisplay(row.channel, row.durationMs, t);
          return (
            <Space size={4} wrap>
              <Text>{display.text}</Text>
              {display.indicator === 'veryShort' && (
                <Tag color="orange">{t('conversationOverview.duration.veryShort')}</Tag>
              )}
              {display.indicator === 'veryLong' && (
                <Tag color="purple">{t('conversationOverview.duration.veryLong')}</Tag>
              )}
            </Space>
          );
        }
      },
      {
        title: t('conversationOverview.columns.customerHelped'),
        dataIndex: 'customerHelped',
        key: 'customerHelped',
        width: 160,
        render: (value: ConversationOverviewListItem['customerHelped']) => (
          <HelpedTag value={value} />
        )
      },
      {
        title: t('conversationOverview.columns.technicalIssue'),
        dataIndex: 'technicalIssue',
        key: 'technicalIssue',
        width: 130,
        render: (value: boolean) => <YesNoTag value={value} />
      },
      {
        title: t('conversationOverview.columns.delay'),
        dataIndex: 'assistantResponseDelayed',
        key: 'delay',
        width: 100,
        render: (value: boolean) => <YesNoTag value={value} />
      },
      {
        title: t('conversationOverview.columns.callbackRequested'),
        dataIndex: 'callbackRequested',
        key: 'callbackRequested',
        width: 140,
        render: (value: boolean) => <YesNoTag value={value} />
      },
      {
        title: t('conversationOverview.columns.humanHandoff'),
        dataIndex: 'humanHandoff',
        key: 'humanHandoff',
        width: 140,
        render: (value: boolean) => <HandoffTag value={value} />
      },
      {
        title: t('conversationOverview.columns.flag'),
        dataIndex: 'flag',
        key: 'flag',
        width: 110,
        render: (flag: ConversationOverviewFlag) => <FlagTag flag={flag} />
      },
      {
        title: t('conversationOverview.columns.summary'),
        dataIndex: 'summary',
        key: 'summary',
        ellipsis: true,
        render: (value: string | null) =>
          value ? (
            <Tooltip title={value} placement="topLeft">
              <Text type="secondary" ellipsis style={{ maxWidth: 280, display: 'block' }}>
                {value}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          )
      },
      {
        title: t('conversationOverview.columns.actions'),
        key: 'actions',
        width: 130,
        fixed: pinColumn('right'),
        render: (_, row) => (
          <Link
            to="/admin/conversation-overview/$threadId"
            params={{ threadId: row.threadId }}
            search={{ completionId: row.completionId ?? undefined }}
          >
            <Button type="text" size="small" icon={<EyeOutlined />}>
              {t('conversationOverview.details')}
            </Button>
          </Link>
        )
      }
    ],
    [clientsById, pinColumn, t, token.colorPrimary, token.colorTextSecondary, token.marginXS, token.marginXXS]
  );

  return (
    <Card
      title={
        <IconCardTitle
          icon={<MessageOutlined />}
          title={t('conversationOverview.level2Title')}
          extra={!loading && <Tag color="blue" style={{ marginInlineEnd: 0 }}>{total}</Tag>}
        />
      }
      styles={{ body: { padding: 0 } }}
    >
      <Table<ConversationOverviewListItem>
        rowKey={(row) => `${row.threadId}:${row.completionId ?? 'legacy'}`}
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={conversations}
        scroll={{ x: 'max-content' }}
        onRow={(row) =>
          row.flag === 'RED'
            ? {
                style: {
                  background: token.colorErrorBg
                }
              }
            : {}
        }
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                isError
                  ? t('conversationOverview.loadError')
                  : t('conversationOverview.emptyConversations')
              }
              style={{ paddingBlock: token.paddingXL }}
            />
          )
        }}
        pagination={{
          style: { paddingInline: token.padding },
          current: page,
          pageSize,
          total,
          showQuickJumper: true,
          showTotal: (paginationTotal, range) =>
            t('conversationOverview.paginationTotal', {
              start: range[0],
              end: range[1],
              total: paginationTotal
            }),
          onChange: (nextPage, nextPageSize) => onPageChange(nextPage, nextPageSize)
        }}
      />
    </Card>
  );
}
